import { useState, useEffect, useMemo } from 'react';
import { studentService, courseService, settingService } from '../services';

/**
 * 财务记录表单的共享业务逻辑
 * @param {Object} initialData - 初始数据（编辑模式使用）
 * @returns {Object} 表单状态和方法
 */
export const useFinanceForm = (initialData = {}) => {
  const [students, setStudents] = useState([]);
  const [allAvailableCourses, setAllAvailableCourses] = useState([]); // 存储所有状态为"可用"的课程
  const [studentActiveCourseIds, setStudentActiveCourseIds] = useState([]); // 学员当前尚有余额的课程ID
  const [selectedStudentId, setSelectedStudentId] = useState(
    initialData.student_id ? String(initialData.student_id) : ''
  );
  const [selectedCourseId, setSelectedCourseId] = useState(
    initialData.course_id ? String(initialData.course_id) : ''
  );
  const [loading, setLoading] = useState(false);
  const [filtering, setFiltering] = useState(false); // 过滤中的状态
  const [settingsLoaded, setSettingsLoaded] = useState(false); // 设置是否加载完成
  const [defaultValues, setDefaultValues] = useState({
    unit_price: '',
    total_hours: '',
    bonus_type: '',
    bonus_hours: '',
    referral_reward: '',
    referral_milestone: ''
  });

  // 支付方式选项
  const paymentMethods = ["微信支付", "支付宝", "现金", "银行卡", "赠送"];

  // 获取今天的日期（格式：YYYY-MM-DD）
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // 加载学生列表（只显示在学的学生）
  useEffect(() => {
    const loadStudents = async () => {
      try {
        setLoading(true);
        const response = await studentService.getList({ page: 1, per_page: 1000 });
        // 只保留student_type为"在学"的学生
        const activeStudents = (response.items || []).filter(
          student => student.student_type === '在学'
        );
        setStudents(activeStudents);
      } catch (error) {
        console.error('获取学生列表失败:', error);
      } finally {
        setLoading(false);
      }
    };
    loadStudents();
  }, []);

  // 加载课程列表（只显示可用的课程）
  useEffect(() => {
    const loadCourses = async () => {
      try {
        setLoading(true);
        const response = await courseService.getList({ page: 1, per_page: 1000 });
        // 只保留status为"可用"的课程
        const activeCourses = (response.items || []).filter(
          course => course.status === '可用'
        );
        setAllAvailableCourses(activeCourses);
      } catch (error) {
        console.error('获取课程列表失败:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCourses();
  }, []);

  // 加载默认系统参数
  useEffect(() => {
    const loadDefaultSettings = async () => {
      try {
        const data = await settingService.getSettings();
        if (data && data.length > 0) {
          const defaults = {};
          data.forEach(s => {
            if (s.key === 'finance_default_unit_price') defaults.unit_price = s.value;
            if (s.key === 'finance_default_total_hours') defaults.total_hours = s.value;
            if (s.key === 'finance_default_bonus_type') defaults.bonus_type = s.value;
            if (s.key === 'finance_default_bonus_hours') defaults.bonus_hours = s.value;
            if (s.key === 'referral_default_reward') defaults.referral_reward = s.value;
            if (s.key === 'reward_referral_milestone') defaults.referral_milestone = s.value;
          });
          if (Object.keys(defaults).length > 0) {
            setDefaultValues(prev => ({ ...prev, ...defaults }));
            setSettingsLoaded(true);
          }
        }
      } catch (error) {
        console.error('获取默认配置失败:', error);
      }
    };
    loadDefaultSettings();
  }, []);

  // 当初始数据更新时同步状态（用于处理异步加载的详情数据）
  useEffect(() => {
    if (initialData.student_id) {
      setSelectedStudentId(String(initialData.student_id));
    }
    if (initialData.course_id) {
      setSelectedCourseId(String(initialData.course_id));
    }
  }, [initialData.student_id, initialData.course_id]);

  // 当选择的学生改变时，获取其尚有余额的课程，用于过滤下拉表
  useEffect(() => {
    const loadStudentActiveBalances = async () => {
      if (!selectedStudentId) {
        setStudentActiveCourseIds([]);
        return;
      }

      try {
        setFiltering(true);
        const accounts = await studentService.getAccounts(selectedStudentId);
        // 提取所有尚有课时余额 (>0) 的课程ID
        const activeIds = (accounts || [])
          .filter(acc => parseFloat(acc.current_hours_balance) > 0)
          .map(acc => acc.course_id);
        
        setStudentActiveCourseIds(activeIds);

        // 如果当前选中的课程在排除列表中，则清除课程选择
        if (selectedCourseId && activeIds.includes(parseInt(selectedCourseId))) {
          setSelectedCourseId('');
        }
      } catch (error) {
        console.error('获取学员账户信息失败:', error);
        setStudentActiveCourseIds([]);
      } finally {
        setFiltering(false);
      }
    };

    // 编辑模式下初始加载不需要自动清除（防止初始化时清除已有的正确数据）
    // 但在新增模式或手动更改学生时需要
    loadStudentActiveBalances();
  }, [selectedStudentId]);

  // 根据学员在学情况动态过滤课程下拉列表
  const filteredCourses = useMemo(() => {
    // 如果是编辑模式（有ID），或者没有选择学生，则不应用“过滤已购课程”的限制逻辑
    if (initialData.id || !selectedStudentId) return allAvailableCourses;
    
    return allAvailableCourses.filter(course => 
      !studentActiveCourseIds.includes(course.id) || 
      (initialData.course_id && course.id === initialData.course_id) // 始终保留当前正在编辑的课程
    );
  }, [allAvailableCourses, studentActiveCourseIds, selectedStudentId, initialData.id, initialData.course_id]);

  // 处理学生选择变化
  const handleStudentChange = (studentId) => {
    setSelectedStudentId(studentId);
  };

  // 处理课程选择变化
  const handleCourseChange = (courseId) => {
    setSelectedCourseId(courseId);
  };

  return {
    students,
    courses: filteredCourses, // 自动暴露过滤后的列表
    selectedStudentId,
    selectedCourseId,
    paymentMethods,
    defaultValues,
    settingsLoaded,
    loading: loading || filtering,
    getTodayDate,
    handleStudentChange,
    handleCourseChange,
  };
};
